import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { api } from '../api/client';
import { useT } from '../i18n/i18n';
import { BackupCodes, TwoFactorSetup, fetchTwoFactor, type TwoFactorStatus } from './twoFactor';

export default function TwoFactorSection() {
  const t = useT();
  const [status, setStatus] = useState<TwoFactorStatus | null>(null);
  const [mode, setMode] = useState<'idle' | 'setup' | 'off' | 'codes'>('idle');
  const [password, setPassword] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    fetchTwoFactor().then(setStatus).catch(() => setStatus(null));
  }, []);
  useEffect(load, [load]);

  async function withPassword(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (mode === 'off') {
        await api<void>('/me/two-factor/off', { method: 'POST', token: true, body: { password } });
        setMode('idle');
        load();
      } else {
        const r = await api<{ backupCodes: string[] }>('/me/two-factor/backup-codes', { method: 'POST', token: true, body: { password } });
        setCodes(r.backupCodes);
      }
      setPassword('');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('That could not be saved just now.'));
    } finally {
      setBusy(false);
    }
  }

  if (!status || !status.available) return null;

  return (
    <div className="card">
      <h2>{t('Two-step sign-in')}</h2>
      {mode === 'setup' ? (
        <TwoFactorSetup onEnabled={() => { setMode('idle'); load(); }} onCancel={() => setMode('idle')} />
      ) : codes ? (
        <BackupCodes codes={codes} onDone={() => { setCodes(null); setMode('idle'); load(); }} />
      ) : mode === 'off' || mode === 'codes' ? (
        <form onSubmit={withPassword} className="spaced" noValidate>
          <p>{mode === 'off' ? t('To turn off two-step sign-in, type your password.') : t('New backup codes replace the old ones. Type your password to make them.')}</p>
          {error && <div className="error" role="alert">{error}</div>}
          <div className="field">
            <label htmlFor="two-factor-password">{t('Password')}</label>
            <input id="two-factor-password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
          </div>
          <div className="toolbar">
            <button type="submit" className="btn" disabled={busy || !password}>
              {busy ? t('Working…') : mode === 'off' ? t('Turn off two-step sign-in') : t('Make new backup codes')}
            </button>
            <button type="button" className="btn ghost" onClick={() => { setMode('idle'); setError(null); setPassword(''); }}>{t('Cancel')}</button>
          </div>
        </form>
      ) : status.enabled ? (
        <>
          <p>{t('On. When you sign in, we also ask for the code from your phone. If you cannot use your phone, you can use a backup code or have a code emailed to you.')}</p>
          <p className="field-hint">{t('Backup codes left: {n}', { n: status.backupCodesLeft })}</p>
          <div className="toolbar">
            <button type="button" className="btn secondary" onClick={() => setMode('codes')}>{t('Make new backup codes')}</button>
            {!status.required && <button type="button" className="btn ghost" onClick={() => setMode('off')}>{t('Turn off two-step sign-in')}</button>}
          </div>
          {status.required && <p className="field-hint">{t('Your school asks parents to keep two-step sign-in on.')}</p>}
        </>
      ) : (
        <>
          <p>{t('Off. Turn it on and, when you sign in, we also ask for a code from an app on your phone. Someone who learns your password still cannot get in.')}</p>
          <div className="toolbar">
            <button type="button" className="btn secondary" onClick={() => setMode('setup')}>{t('Set up two-step sign-in')}</button>
          </div>
        </>
      )}
    </div>
  );
}
